// The playbooks the web shows are the same files the MCP prompts and `start_playbook` serve
// (capability_catalog/playbooks, embedded by capability_catalog::playbooks). Order matches that module.
import budgetVsActual from '../../../capability_catalog/playbooks/budget_vs_actual.json'
import buildClientWorkbook from '../../../capability_catalog/playbooks/build_client_workbook.json'
import clientOnboarding from '../../../capability_catalog/playbooks/client_onboarding.json'
import investorReadiness from '../../../capability_catalog/playbooks/investor_readiness.json'
import meetingPrepFollowUp from '../../../capability_catalog/playbooks/meeting_prep_follow_up.json'
import monthEndCloseReview from '../../../capability_catalog/playbooks/month_end_close_review.json'
import monthlyBoardPack from '../../../capability_catalog/playbooks/monthly_board_pack.json'
import thirteenWeekCash from '../../../capability_catalog/playbooks/thirteen_week_cash.json'

export interface PlaybookStep {
  title: string
  prompt: string
  capability: string
  /** What the FuseGrid tool returns, and nothing more. */
  returns: string
  /** What the assistant does with it: its own work, not FuseGrid's. */
  assistant_then: string
  what_you_check: string
  why: string
}
export interface Playbook {
  id: string
  title: string
  summary: string
  job: string
  audience: 'advisor' | 'owner'
  practice_on_demo: boolean
  next: string
  steps: PlaybookStep[]
}

export const PLAYBOOKS: readonly Playbook[] = [
  clientOnboarding, monthEndCloseReview, monthlyBoardPack, thirteenWeekCash,
  budgetVsActual, meetingPrepFollowUp, investorReadiness, buildClientWorkbook,
] as Playbook[]

export const playbookById = (id: string) => PLAYBOOKS.find(playbook => playbook.id === id)

/** Mirrors `Playbook::render_prompt`: fills `{company}` and `{period}`. */
export function renderPrompt(template: string, company: string, period?: string | null): string {
  return template.replaceAll('{company}', company).replaceAll('{period}', period?.trim() ? period.trim() : 'the latest closed month')
}

/** Mirrors `Playbook::render_script`: the whole playbook as one message for Claude or ChatGPT. */
export function renderScript(playbook: Playbook, company: string, period?: string | null): string {
  let out = `Run the FuseGrid playbook "${playbook.title}" for ${company}. ${playbook.summary}\n\nWork one step at a time. Before each step, say what you will do and what I should check. After each step, stop and wait for me to confirm before moving on.\n`
  playbook.steps.forEach((step, index) => {
    out += `\nStep ${index + 1}: ${step.title}\n- Run: ${renderPrompt(step.prompt, company, period)} (FuseGrid tool \`${step.capability}\`)\n- FuseGrid returns: ${renderPrompt(step.returns, company, period)}\n- Then you: ${renderPrompt(step.assistant_then, company, period)}\n- What I check: ${renderPrompt(step.what_you_check, company, period)}\n`
  })
  return out
}

/** `/playbooks` → null, `/playbooks/<id>` → id. */
export function parsePlaybookRoute(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean)
  return parts[0] === 'playbooks' && parts[1] ? decodeURIComponent(parts[1]) : null
}

/** Picks the first demo company from a `list_companies` result, whatever key the executor names it by. */
export function firstCompanyName(result: unknown): string | null {
  const companies = (result as { companies?: unknown })?.companies
  if (!Array.isArray(companies) || companies.length === 0) return null
  const first = companies[0] as Record<string, unknown>
  const name = first.display_name ?? first.name ?? first.company_key
  return typeof name === 'string' && name.trim() ? name : null
}
